import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { Permission, Res } from 'core/api';
import { Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { PosScreen, Header, Content, LargeDeviceOnly, Footer, PhoneOnly, BackActionButton, Filler, AcceptActionButton } from 'pos/ui';
import { Command, Electron } from 'pos/common';
import { Modal } from 'core/ui-next/modal';
import { VStack, Text, Box } from '@chakra-ui/react';
import { BasicButton, SvgIcon, useEffectOnce } from 'core/ui-next';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { z } from 'zod';


type Props = { currentFriendlyName: string } & Modal.Actions<string>;

const stateSchema = z.object({
	friendlyName: z.string()
});

type State = z.infer<typeof stateSchema>;

function GetFriendlyName(props: Props): React.JSX.Element {

	const { currentFriendlyName, close, cancel } = props;
	const formRef = React.useRef<FormRef<State>>(null);
	const { Form, Input } = useControlsFor<State>();

	useEffectOnce(() => {
		formRef.current?.setFocus?.('friendlyName');
		if (Electron.isEnabled())
			void Electron.Keyboard.show();

		return () => {
			if (Electron.isEnabled())
				void Electron.Keyboard.hide();
		};
	});

	const onAccept = async () => {
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;
		close(result.value.friendlyName);
	};

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	return <PosScreen screenId='SET_TICKET_FRIENDLY_NAME' size={'small'}>
		<Header title={Res.Web.SET_TICKET_FRIENDLY_NAME()} onClose={cancel} />
		<Content>
			<VStack paddingX={'6'} paddingY={'9'} h='full' w='full' gap={'6'}>
				<VStack gap={'4'} justifyItems={'center'} w='full'>
					<SvgIcon src={Images.TicketFriendlyName} w={'12'} h={'12'} />
					<VStack gap={'2'} justifyItems={'center'} w='full' textAlign={'center'}>
						<Text fontSize={'md'} fontWeight={'bold'}>
							{Res.Web.SET_TICKET_FRIENDLY_NAME()}
						</Text>
					</VStack>
				</VStack>
				<VStack w={'full'} justifyContent={'center'}>
					<Box w={'full'}>
						<Form ref={formRef} initialValue={{ friendlyName: currentFriendlyName }} schema={stateSchema}>
							<Input linkTo='friendlyName' placeholder={Res.Web.SET_TICKET_FRIENDLY_NAME()} onKeyDown={UI.whenEnterKey(onAccept)} />
						</Form>
					</Box>
				</VStack>
				<LargeDeviceOnly>
					<BasicButton type='success' text={Res.Common.OK_BTN()} onClick={onAccept} />
				</LargeDeviceOnly>
			</VStack>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
				<Filler />
				<AcceptActionButton action={onAccept} />
			</PhoneOnly>
		</Footer>
	</PosScreen>;
}


declare global {
	interface CommandType {
		setTicketFriendlyName: (params: Params) => Promise<Result>
	}
}

type Params = {
	currentFriendlyName: string;
	setFriendlyName: (name: string) => Promise<void>
};

Command.register('setTicketFriendlyName', Preconditions.ensure(Permission.Ticket_SetFriendlyName), async (params: Params): Promise<Result> => {
	const friendlyName = await Modal.show(GetFriendlyName, { currentFriendlyName: params.currentFriendlyName });
	if (!friendlyName.ok)
		return Result.error();

	await params.setFriendlyName(friendlyName.value);
	return Result.ok();
});